// Central diagnostics for Firebase failures.
//
// `permission-denied` arrives as the bare sentence "Missing or insufficient
// permissions." — it names neither the query that was refused nor the identity
// it was sent under, which is exactly what is needed while the security rules
// are being written. Everything the rules evaluate against is gathered here and
// printed alongside the error.
//
// Deliberately imports nothing from `firebase.js`: that module logs through this
// one, and the reverse import would close a cycle. The auth/app state arrives
// instead from FirebaseContextProvider through `setErrorContext`.

let context = {};

// Called by FirebaseContextProvider whenever the identity or the organization
// changes, so a failure landing at any moment describes the state as of then.
export const setErrorContext = (next) => {
  context = next ?? {};
};

const permissionCodes = [
  'permission-denied',
  'functions/permission-denied',
  'storage/unauthorized',
  'unauthenticated',
  'functions/unauthenticated',
];

const isPermissionError = (error) => permissionCodes.includes(error?.code);

// Only the checks that actually fired — a list of everything that could be
// wrong is noise, the point is to name the one thing that is.
// `orgScoped` is declared by the call site rather than guessed from `filters`:
// plenty of legitimate reads carry no organization filter at all (the self doc,
// tourStatuses, chats by membership, a parent's own links), and warning about a
// missing filter there sends the reader after a constraint that must not exist.
const likelyCauses = ({ filters, orgScoped }) => {
  const {
    uid,
    authUid,
    isUidOptimistic,
    role,
    activeOrg,
    myOrgs = [],
    isSelfLoaded,
    isOrgResolved,
    isOrgConfirmed,
    syncedOrg,
  } = context;

  const causes = [];
  const filtersByOrg = filters?.some((f) => f.includes('organizations'));

  if (!authUid) {
    causes.push(
      'Not signed in (auth.currentUser is null) — the query went out anonymous',
    );
  } else if (uid && uid !== authUid) {
    causes.push(
      `Identity changed mid-flight: the request used ${uid}, auth now holds ${authUid}`,
    );
  }

  if (isUidOptimistic) {
    causes.push(
      'uid is still the optimistic guess from localStorage — auth has not confirmed it yet',
    );
  }

  if (!isSelfLoaded) {
    causes.push(
      'The users/{uid} document has not loaded — role and organization are unknown to the client',
    );
  }

  if (!isOrgResolved) {
    causes.push('Organization not resolved yet — the query may be premature');
  }

  if (orgScoped) {
    if (!activeOrg) {
      causes.push(
        'Query is organization-scoped but activeOrg is null/empty — the filter went out undefined',
      );
    } else if (!filtersByOrg) {
      causes.push(
        'Query carries no organization filter while an organization is active — org-scoped rules refuse unfiltered reads',
      );
    } else if (!isOrgConfirmed) {
      // The filter looks right, which is exactly why this one is worth naming:
      // the cache answers without rules and without auth, so the organization
      // it carries is a guess until the server confirms the self document,
      // while the rules read users/{uid}.organization off the server.
      causes.push(
        `The active organization is not server-confirmed — the query used "${activeOrg}" while the last value the server stated was ${
          syncedOrg ? `"${syncedOrg}"` : 'none'
        }`,
      );
    }
  }

  if (activeOrg && myOrgs.length && !myOrgs.includes(activeOrg)) {
    causes.push(
      `activeOrg "${activeOrg}" is not among the user's organizations [${myOrgs.join(', ')}]`,
    );
  }

  if (authUid && !role) causes.push('No role on the user document');

  return causes;
};

export const logFirebaseError = (error, descriptor = {}) => {
  const { operation, path, filters, extra } = descriptor;
  const permission = isPermissionError(error);

  const label = [
    '[Firebase]',
    error?.code ?? error?.name ?? 'error',
    operation || path ? `— ${operation ?? ''}${path ? ` ${path}` : ''}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  // A denial is the thing being debugged right now, so it opens expanded;
  // everything else stays folded to keep the console readable.
  const open = permission ? console.group : console.groupCollapsed;
  open.call(console, label);

  console.error(error);

  console.log('request', {
    operation,
    path,
    filters,
    ...(extra ? { extra } : {}),
  });

  // Presence and field names only, never the values: this block is read off
  // shared screens and pasted into support tickets, and the console is no place
  // for an address or a decoded token. `uid` identifies the user either way.
  console.log('identity', {
    uid: context.uid,
    authUid: context.authUid,
    hasEmail: !!context.email,
    emailVerified: context.emailVerified,
    isUidOptimistic: context.isUidOptimistic,
    claimKeys: context.claims ? Object.keys(context.claims) : null,
    tokenIssuedAt: context.tokenIssuedAt,
    tokenExpiresAt: context.tokenExpiresAt,
  });

  console.log('app state', {
    role: context.role,
    activeOrg: context.activeOrg,
    myOrgs: context.myOrgs,
    teams: context.teams,
    isAuthResolved: context.isAuthResolved,
    isSelfLoaded: context.isSelfLoaded,
    isSelfSynced: context.isSelfSynced,
    isOrgResolved: context.isOrgResolved,
    isOrgConfirmed: context.isOrgConfirmed,
    syncedOrg: context.syncedOrg,
  });

  if (error?.details) console.log('details', error.details);

  if (permission) {
    const causes = likelyCauses(descriptor);
    if (causes.length) {
      console.warn(
        `likely cause:\n${causes.map((c) => `  • ${c}`).join('\n')}`,
      );
    } else {
      console.warn(
        'Client state looks consistent — the rules themselves most likely reject this query shape',
      );
    }
  }

  console.groupEnd();
};
